/**
 * Koi Market — global floating widgets: back to top (unit 18) and live chat (unit 16).
 *
 * `.mockup/script.js:565-597` · `snippets/back-to-top.liquid` · `snippets/live-chat.liquid`
 *
 * The mockup reads `window.scrollY` and listens on `window`. Neither works in
 * this theme above 990px: `assets/base.css` puts the page into a squeeze layout
 * where `html` and `body` are `height: 100dvh; overflow: hidden` and
 * `.page-wrapper` is the element that actually scrolls, so `window.scrollY` is
 * permanently 0 and a `window` scroll listener never fires (progress-log gap
 * G1). `@theme/scroll-container` abstracts that, and the listener is rebound
 * when the viewport crosses the breakpoint — the same approach
 * `assets/header.js` takes for its sticky behaviour.
 *
 * Both widgets are rendered from `layout/theme.liquid`, so neither is inside a
 * section and `shopify:section:load` cannot bring a new one into the page.
 * Changing a theme setting in the editor reloads the document, which re-runs
 * this module, so there is nothing to re-initialise.
 */

import { getScrollTop, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

/** Scroll distance past which the back-to-top control appears (`.mockup/script.js:596`). */
const REVEAL_AT = 600;

/* ==========================================================================
   Back to top
   ========================================================================== */

/** @type {HTMLElement | null} */
const toTop = document.querySelector('#to-top');

/** @type {EventTarget | null} */
let scrollTarget = null;

/** @type {number | null} */
let scrollRafId = null;

function paintToTop() {
  scrollRafId = null;
  toTop?.classList.toggle('is-visible', getScrollTop() > REVEAL_AT);
}

/* rAF-throttled so a scroll burst repaints once per frame, as header.js does. */
const handleScroll = () => {
  if (scrollRafId !== null) return;
  scrollRafId = requestAnimationFrame(paintToTop);
};

if (toTop) {
  scrollTarget = getScrollEventTarget();
  scrollTarget.addEventListener('scroll', handleScroll, { passive: true });

  /* The scroll container swaps between `.page-wrapper` and the document root at
     990px, which leaves the binding made above stale after a resize. */
  scrollContainerMediaQuery.addEventListener('change', () => {
    scrollTarget?.removeEventListener('scroll', handleScroll);
    scrollTarget = getScrollEventTarget();
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    paintToTop();
  });

  /* Paint once on load: a restored scroll position (see scroll-container.js) or
     a mid-page reload both start already past the threshold. */
  paintToTop();
}

/* ==========================================================================
   Live chat
   ========================================================================== */

/** @type {HTMLButtonElement | null} */
const chatFab = document.querySelector('#live-chat-fab');
/** @type {HTMLElement | null} */
const chatPanel = document.querySelector('#live-chat-panel');
/** @type {HTMLButtonElement | null} */
const chatClose = document.querySelector('#live-chat-close');

/**
 * Opens or closes the channel panel and keeps `aria-expanded` in step with it.
 *
 * @param {boolean} open
 */
function setChatOpen(open) {
  if (!chatFab || !chatPanel) return;

  chatPanel.hidden = !open;
  chatFab.setAttribute('aria-expanded', String(open));
}

if (chatFab && chatPanel) {
  chatFab.addEventListener('click', () => {
    const opening = chatPanel.hidden;
    setChatOpen(opening);

    /* Take keyboard users straight to the channels rather than leaving them on the FAB. */
    if (opening) chatPanel.querySelector('.live-chat__option')?.focus();
  });

  chatClose?.addEventListener('click', () => {
    setChatOpen(false);
    /* Focus would otherwise be lost on the now-hidden close button, dropping a
       keyboard user back to the top of the document. */
    chatFab.focus();
  });

  /* The mockup's script has no Escape handler. A disclosure that can be opened
     from the keyboard should be closable the same way (guidelines rule 7b.2). */
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || chatPanel.hidden) return;

    const active = document.activeElement;
    const focusWasHere = active === chatFab || (active instanceof Node && chatPanel.contains(active));

    setChatOpen(false);
    if (focusWasHere) chatFab.focus();
  });
}
